// Measurement-only: counts how many component fibers actually re-rendered per
// commit so the theme-swap re-render cascade can be quantified. Gated by
// GATSBY_PROFILE and installed before React renders (from gatsby-browser).
//
// React already exposes per-commit fiber data to the React DevTools backend via
// the __REACT_DEVTOOLS_GLOBAL_HOOK__ global. We install a minimal hook (or chain
// onto a real one) and, on each commit, tally the fibers that performed work —
// exactly the signal DevTools uses to mark a component as "rendered". The count
// goes into the themePerf store; this module never logs.

import { THEME_PERF_ENABLED, recordComponentsRendered } from 'lib/themePerf'

// React sets this flag (0b1) on a fiber that performed work during a commit.
// PerformedWork has been the first flag (value 1) since React 16.8.
const PERFORMED_WORK = 0b1

// Fiber tags for the component kinds whose re-render we care about. Host (DOM)
// nodes and text are excluded so the count reflects React components, which is
// what the ThemeProvider swap re-renders (and what the CSS-vars refactor will
// stop re-rendering).
const COMPONENT_TAGS = new Set([
  0, // FunctionComponent
  1, // ClassComponent
  9, // ContextConsumer
  11, // ForwardRef
  14, // MemoComponent
  15, // SimpleMemoComponent
])

const countRenderedFibers = (root: any): number => {
  const start = root?.current
  if (!start) return 0

  let count = 0
  // Iterative DFS so a deep tree can't blow the stack.
  const stack: any[] = [start]
  while (stack.length) {
    const fiber = stack.pop()
    if (!fiber) continue
    if (
      (fiber.flags & PERFORMED_WORK) === PERFORMED_WORK &&
      COMPONENT_TAGS.has(fiber.tag)
    ) {
      count += 1
    }
    if (fiber.child) stack.push(fiber.child)
    if (fiber.sibling) stack.push(fiber.sibling)
  }
  return count
}

interface DevtoolsHook {
  supportsFiber: boolean
  renderers: Map<number, unknown>
  inject: (renderer: unknown) => number
  onCommitFiberRoot: (id: number, root: any, ...rest: unknown[]) => void
  onPostCommitFiberRoot: (id: number, root: unknown) => void
  onCommitFiberUnmount: (id: number, fiber: unknown) => void
  checkDCE?: () => void
}

export const installThemePerfDevtoolsHook = () => {
  if (!THEME_PERF_ENABLED || typeof window === 'undefined') return

  const w = window as unknown as {
    __REACT_DEVTOOLS_GLOBAL_HOOK__?: Partial<DevtoolsHook>
  }

  const tally = (root: any) => {
    try {
      recordComponentsRendered(countRenderedFibers(root))
    } catch {
      // Counting is best-effort and must never break React's commit phase.
    }
  }

  const existing = w.__REACT_DEVTOOLS_GLOBAL_HOOK__

  if (existing && typeof existing.onCommitFiberRoot === 'function') {
    // A real DevTools backend (or another hook) is present — chain onto it so we
    // don't disable it.
    const prev = existing.onCommitFiberRoot.bind(existing)
    existing.onCommitFiberRoot = (id, root, ...rest) => {
      tally(root)
      prev(id, root, ...rest)
    }
    return
  }

  if (existing) {
    // A partial hook exists without commit handling — augment it in place.
    existing.onCommitFiberRoot = (_id, root) => tally(root)
    return
  }

  let nextID = 0
  const hook: DevtoolsHook = {
    supportsFiber: true,
    renderers: new Map(),
    inject: (renderer) => {
      const id = nextID++
      hook.renderers.set(id, renderer)
      return id
    },
    onCommitFiberRoot: (_id, root) => tally(root),
    onPostCommitFiberRoot: () => {},
    onCommitFiberUnmount: () => {},
    checkDCE: () => {},
  }
  w.__REACT_DEVTOOLS_GLOBAL_HOOK__ = hook
}
